import React, { useState, useEffect, useCallback, useMemo, Fragment } from 'react'

import { number, string, bool, func, shape, instanceOf } from 'prop-types'

const DEFAULT_TIME_LABELS = {
  day: 'd',
  hour: 'h',
  minute: 'm',
  second: 's',
}

const parseTime = (milliseconds) => {
  const MS_PER_SECOND = 1000
  const MS_PER_MINUTE = 60000
  const MS_PER_HOUR = 3600000
  const MS_PER_DAY = 86400000

  let remaining = milliseconds

  const days = Math.floor(remaining / MS_PER_DAY)
  remaining -= days * MS_PER_DAY

  const hours = Math.floor(remaining / MS_PER_HOUR)
  remaining -= hours * MS_PER_HOUR

  const minutes = Math.floor(remaining / MS_PER_MINUTE)
  remaining -= minutes * MS_PER_MINUTE

  const seconds = Math.floor(remaining / MS_PER_SECOND)

  return { days, hours, minutes, seconds }
}

function Countdown({
  targetDate,
  interval = 1000,
  startDelay = 0,
  format = { day: true, hour: true, minute: true, second: true },
  timeSeparator = ' ',
  leadingZero = false,
  showLabels = false,
  timeLabels = DEFAULT_TIME_LABELS,
  onFinished = null,
  className = '',
}) {
  const [remainingTime, setRemainingTime] = useState(0)
  const [status, setStatus] = useState('not_started')

  const calculateRemainingTime = useCallback(() => {
    return Math.max(0, targetDate - Date.now())
  }, [targetDate])

  const addLeadingZero = useCallback((value) => {
    return value < 10 ? `0${value}` : value
  }, [])

  const getLabel = useCallback((unit) => {
    if (!showLabels) return ''
    return timeLabels[unit]
  }, [showLabels, timeLabels])

  const tick = useCallback(() => {
    const remaining = calculateRemainingTime()
    setRemainingTime(remaining)

    if (remaining <= 0) {
      setStatus('finished')
      if (onFinished) {
        onFinished()
      }
    }
  }, [calculateRemainingTime, onFinished])

  useEffect(() => {
    const startTimer = setTimeout(() => {
      setStatus('running')
      tick()

      const intervalId = setInterval(tick, interval)

      return () => clearInterval(intervalId)
    }, startDelay)

    return () => clearTimeout(startTimer)
  }, [startDelay, interval, tick])

  const timeUnits = useMemo(() => {
    if (remainingTime <= 0) return []

    const { days, hours, minutes, seconds } = parseTime(remainingTime)
    const units = []

    if (format.day) {
      const value = leadingZero ? addLeadingZero(days) : days
      units.push({
        key: 'day',
        value,
        label: getLabel('day'),
        className: 'countdown__day',
      })
    }

    if (format.hour) {
      const value = leadingZero ? addLeadingZero(hours) : hours
      units.push({
        key: 'hour',
        value,
        label: getLabel('hour'),
        className: 'countdown__hour',
      })
    }

    if (format.minute) {
      const value = leadingZero ? addLeadingZero(minutes) : minutes
      units.push({
        key: 'minute',
        value,
        label: getLabel('minute'),
        className: 'countdown__minute',
      })
    }

    if (format.second) {
      const value = leadingZero ? addLeadingZero(seconds) : seconds
      units.push({
        key: 'second',
        value,
        label: getLabel('second'),
        className: 'countdown__second',
      })
    }

    return units
  }, [remainingTime, format, leadingZero, addLeadingZero, getLabel])

  if (status === 'not_started') {
    return null
  }

  return (
    <div className={`countdown ${className}`}>
      {timeUnits.map((unit, index) => (
        <Fragment key={unit.key}>
          <span className={unit.className}>
            {unit.value}
            {unit.label && <span className="countdown__label">{unit.label}</span>}
          </span>
          {index < timeUnits.length - 1 && timeSeparator && (
            <span className="countdown__separator">{timeSeparator}</span>
          )}
        </Fragment>
      ))}
    </div>
  )
}

Countdown.propTypes = {
  targetDate: instanceOf(Date).isRequired,
  interval: number,
  startDelay: number,
  format: shape({
    day: bool,
    hour: bool,
    minute: bool,
    second: bool,
  }),
  timeSeparator: string,
  leadingZero: bool,
  showLabels: bool,
  timeLabels: shape({
    day: string,
    hour: string,
    minute: string,
    second: string,
  }),
  onFinished: func,
  className: string,
}

export default Countdown
